// @ts-nocheck
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  TextField,
  Grid,
  useTheme,
  alpha,
} from '@mui/material';
import { ReactComponent as ChevronRightIcon } from '../../../../assets/Icons/lucideIcons/chevron-right.svg';
import { lightTheme } from '../../../../theme/light';

/**
 * Shows attribute information for the selected column in the sidebar.
 * Displays 4 disabled fields: label, name, id, data_type.
 *
 * Data sources (in priority order):
 * 1. attribute_schema from API response (edit mode)
 * 2. _local_* fields stored at column creation time (first-time creation)
 * 3. attribute_id / attribute_label from attribute_config
 */
const ConfigColumnDetails = ({ selectedColumnName, schema, attributeList }) => {
  const theme = useTheme();

  const disabledFieldSx = {
    '& .MuiInputBase-root.Mui-disabled': {
      bgcolor: alpha(
        theme?.palette?.action?.disabledBackground ||
          lightTheme?.palette?.colorPicker?.black,
        0.4
      ),
    },
  };

  const attributeInfo = useMemo(() => {
    if (!selectedColumnName || !schema?.columnSettings) return null;

    const settings = schema.columnSettings[selectedColumnName];
    if (!settings) return null;

    const config = settings?.attribute_config;
    const fullAttr = attributeList?.find(
      (attribute) => String(attribute?.id) === String(config?.attribute_id)
    );
    const apiSchema = fullAttr || config?.attribute_schema || {};

    return {
      label: apiSchema?.label || config?.attribute_label || '',
      name: apiSchema?.name || config?._local_name || '',
      id: config?.attribute_id || '',
      dataType: apiSchema?.data_type || config?._local_data_type || '',
    };
  }, [selectedColumnName, schema, attributeList]);

  // No column selected state
  if (!selectedColumnName || !attributeInfo) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          gap: 1.5,
          color: 'text.disabled',
        }}
      >
        <ChevronRightIcon
          width={48}
          height={48}
          style={{
            color: alpha(lightTheme?.palette?.text?.disabled, 0.5),
          }}
        />
        <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
          Select a column
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: 'text.disabled', textAlign: 'center', maxWidth: 280 }}
        >
          Choose a column from the left to see its label, name, id, and data
          type.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Section header */}
      <Typography
        variant="body3"
        sx={{
          display: 'block',
          mb: 2,
        }}
      >
        Attribute Information
      </Typography>

      <Grid container spacing={2}>
        {/* Attribute Label */}
        <Grid size={6}>
          <TextField
            label="Attribute Label"
            value={attributeInfo.label}
            disabled
            fullWidth
            size="small"
            sx={disabledFieldSx}
            slotProps={{
              inputLabel: { shrink: true },
            }}
          />
        </Grid>

        {/* Attribute Name */}
        <Grid size={6}>
          <TextField
            label="Attribute Name"
            value={attributeInfo.name}
            disabled
            fullWidth
            size="small"
            sx={disabledFieldSx}
            slotProps={{
              inputLabel: { shrink: true },
            }}
          />
        </Grid>

        {/* Attribute ID */}
        <Grid size={6}>
          <TextField
            label="Attribute ID"
            value={attributeInfo.id}
            disabled
            fullWidth
            size="small"
            sx={disabledFieldSx}
            slotProps={{
              inputLabel: { shrink: true },
            }}
          />
        </Grid>

        {/* Attribute Data Type */}
        <Grid size={6}>
          <TextField
            label="Attribute Data Type"
            value={attributeInfo.dataType}
            disabled
            fullWidth
            size="small"
            sx={disabledFieldSx}
            slotProps={{
              inputLabel: { shrink: true },
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

ConfigColumnDetails.propTypes = {
  selectedColumnName: PropTypes.string,
  schema: PropTypes.shape({
    columnSettings: PropTypes.shape({}),
  }),
  attributeList: PropTypes.arrayOf(PropTypes.shape({})),
};

export default ConfigColumnDetails;
